import { ContentMasterWawiExtendedFieldView } from '../api/generated/models/ContentMasterWawiExtendedFieldView';

/**
 * Discriminator-aware helpers for the new unified `wawiExtendedFields` array
 * returned on `GET /content-master/product`. The previous shape (separate
 * `wawiArticleAttributes` and `wawiCharacteristics`, plus a free-form
 * `isCustomField` flag) is gone - the backend now ships everything in one
 * ordered array with `type ∈ {"attribut", "eigene", "merkmal"}`.
 *
 * UI rule (per backend spec): render three separate sections in this order:
 *   1. Wawi attributes (`type === 'attribut'`)
 *   2. Eigene Felder / Custom fields (`type === 'eigene'`)
 *   3. Merkmale (`type === 'merkmal'`)
 *
 * Backend already sorts items, so callers MUST preserve API order - these
 * helpers all use `.filter()` which is stable.
 *
 * Multi-value Merkmal contract (current API):
 *   - Every Merkmal is represented by ONE entry per `kMerkmal`. `fieldApiName`
 *     is always `wawiMerkmal_<kMerkmal>`. Values are sent / received as a
 *     comma-separated string of `kMerkmalWert` ids (e.g. "42,44,68"), sorted
 *     ASC, deduplicated, no spaces required. An empty string means
 *     "no values assigned" (cleared).
 *   - Legacy keys (`wawiMerkmalWert_<kMerkmalWert>`) are DEPRECATED - they may
 *     still appear in `draft` / `versions` for previously-saved products and,
 *     in rare orphan cases, in `wawiExtendedFields`. UI must ignore them on
 *     read and never produce them on write.
 */

const WAWI_MERKMAL_WERT_LEGACY_API_PREFIX = 'wawiMerkmalWert_';

export const WAWI_FIELD_TYPE = ContentMasterWawiExtendedFieldView.type;

/** True iff item is `attribut` or `eigene` - both share the same shape and editor. */
export function isAttributeLikeField(
  f: ContentMasterWawiExtendedFieldView
): boolean {
  return (
    f.type === ContentMasterWawiExtendedFieldView.type.ATTRIBUT ||
    f.type === ContentMasterWawiExtendedFieldView.type.EIGENE
  );
}

export function isMerkmalField(
  f: ContentMasterWawiExtendedFieldView
): boolean {
  return f.type === ContentMasterWawiExtendedFieldView.type.MERKMAL;
}

/**
 * A deprecated legacy `wawiMerkmalWert_<id>` API key. Used both for entries
 * inside `wawiExtendedFields` (orphan `kMerkmalWert` without a `kMerkmal`
 * binding - extremely rare edge case) and for stale keys that may still be
 * present in `draft` / `versions` until the product is re-saved.
 */
export function isLegacyMerkmalWertApiKey(key: string | null | undefined): boolean {
  return typeof key === 'string' && key.startsWith(WAWI_MERKMAL_WERT_LEGACY_API_PREFIX);
}

/** Orphan Merkmal entry - `type === 'merkmal'` but the legacy API key shape. */
export function isOrphanMerkmalField(
  f: ContentMasterWawiExtendedFieldView
): boolean {
  return isMerkmalField(f) && isLegacyMerkmalWertApiKey(f.fieldApiName ?? '');
}

/** All `attribut` items (regular Wawi article attributes), API order preserved. */
export function selectArticleAttributes(
  fields: ContentMasterWawiExtendedFieldView[] | null | undefined
): ContentMasterWawiExtendedFieldView[] {
  if (!fields) {
    return [];
  }
  return fields.filter(
    (f) => f.type === ContentMasterWawiExtendedFieldView.type.ATTRIBUT
  );
}

/** All `eigene` items (Eigene Felder / Custom Fields), API order preserved. */
export function selectEigeneFelder(
  fields: ContentMasterWawiExtendedFieldView[] | null | undefined
): ContentMasterWawiExtendedFieldView[] {
  if (!fields) {
    return [];
  }
  return fields.filter(
    (f) => f.type === ContentMasterWawiExtendedFieldView.type.EIGENE
  );
}

/**
 * All `merkmal` items, API order preserved. Orphan entries (legacy
 * `wawiMerkmalWert_<id>` API key - kMerkmalWert without a kMerkmal binding)
 * are filtered out: per the multi-value contract the UI only renders
 * editors for proper `wawiMerkmal_<kMerkmal>` entries.
 */
export function selectMerkmale(
  fields: ContentMasterWawiExtendedFieldView[] | null | undefined
): ContentMasterWawiExtendedFieldView[] {
  if (!fields) {
    return [];
  }
  return fields.filter(
    (f) =>
      f.type === ContentMasterWawiExtendedFieldView.type.MERKMAL &&
      !isOrphanMerkmalField(f)
  );
}

/**
 * Best-effort textual representation of an attribute-like value (matches the
 * old `attributeWawiDisplayValue`). Prefers `displayValue` (already rendered
 * by the backend), then falls back to the typed columns.
 */
export function attributeDisplayValue(
  attr: ContentMasterWawiExtendedFieldView
): string {
  if (typeof attr.displayValue === 'string' && attr.displayValue.length > 0) {
    return attr.displayValue;
  }
  if (attr.nDatenTyp === 0 && attr.nWertInt != null) {
    return String(attr.nWertInt);
  }
  if (attr.nDatenTyp === 1 && typeof attr.fWertDecimal === 'string') {
    return attr.fWertDecimal;
  }
  if (attr.nDatenTyp === 2 && typeof attr.cWertVarchar === 'string') {
    return attr.cWertVarchar;
  }
  if (attr.nDatenTyp === 3 && typeof attr.dWertDateTime === 'string') {
    return attr.dWertDateTime;
  }
  return '';
}

/**
 * AI generation eligibility for an attribute-like field. Same rule as
 * before: only varchar (`nDatenTyp === 2`) non-readonly fields with a stable
 * API key can be AI-generated.
 */
export function canAiGenerateAttributeLike(
  attr: ContentMasterWawiExtendedFieldView
): boolean {
  return (
    isAttributeLikeField(attr) &&
    attr.nDatenTyp === 2 &&
    attr.isReadOnly !== true &&
    typeof attr.fieldApiName === 'string' &&
    attr.fieldApiName.length > 0
  );
}

/**
 * AI eligibility for a Merkmal: must be a non-orphan entry with at least one
 * valid `kMerkmalWert` option. Orphan `wawiMerkmalWert_*` entries are
 * deprecated and never AI-generatable.
 */
export function canAiGenerateMerkmal(
  m: ContentMasterWawiExtendedFieldView
): boolean {
  if (!isMerkmalField(m) || !m.fieldApiName || isOrphanMerkmalField(m)) {
    return false;
  }
  const opts = m.merkmalWertOptions?.filter((o) => o.kMerkmalWert != null);
  return (opts?.length ?? 0) > 0;
}

/**
 * Display label for an attribute-like field. Mirrors legacy behaviour: prefer
 * the technical id `cAttributId` (it's what JTL admins recognise in their
 * tAttribut config), fall back to the localized `name`, then to the API key.
 */
export function attributeLikeLabel(
  attr: ContentMasterWawiExtendedFieldView,
  fieldApiName: string
): string {
  const name = attr.name?.trim();
  if (name) {
    return name;
  }
  const cid = attr.cAttributId?.trim();
  if (cid) {
    return cid;
  }
  return fieldApiName;
}

/**
 * Display label for a Merkmal. The backend may not always populate
 * `merkmalName`; fall back to the API key for diagnostics rather than render
 * an empty cell.
 */
export function merkmalLabel(
  m: ContentMasterWawiExtendedFieldView,
  fieldApiName: string
): string {
  const name = m.merkmalName?.trim();
  return name || fieldApiName;
}

/**
 * Distinct, ordered list of `fieldApiName`s across all extended fields. Orphan
 * Merkmal entries (legacy `wawiMerkmalWert_*` API key) are skipped because
 * they share the deprecated shape and the UI must neither initialize editor
 * state nor send save-draft payloads for them.
 */
export function collectExtendedFieldKeys(
  fields: ContentMasterWawiExtendedFieldView[] | null | undefined
): string[] {
  if (!fields) {
    return [];
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const f of fields) {
    if (!f.fieldApiName) {
      continue;
    }
    if (isOrphanMerkmalField(f)) {
      continue;
    }
    if (seen.has(f.fieldApiName)) {
      continue;
    }
    seen.add(f.fieldApiName);
    out.push(f.fieldApiName);
  }
  return out;
}

/* -------------------------------------------------------------------------- */
/* Multi-value Merkmal helpers                                                */
/* -------------------------------------------------------------------------- */

/**
 * Split a comma-separated `kMerkmalWert` list into trimmed, deduplicated, ASC-
 * sorted numeric-string ids. Pure UI-side normalisation that mirrors what the
 * backend silently does on save.
 *
 *   - Whitespace around commas is allowed and stripped.
 *   - Empty / whitespace-only fragments are dropped.
 *   - Non-numeric fragments are dropped (id space is integer).
 *   - Empty input → empty array (which serialises back to "" = "clear all").
 */
export function parseMerkmalCsvIds(csv: string | null | undefined): string[] {
  if (typeof csv !== 'string' || !csv.length) {
    return [];
  }
  const seen = new Set<string>();
  const out: number[] = [];
  for (const fragment of csv.split(',')) {
    const trimmed = fragment.trim();
    if (!trimmed || !/^-?\d+$/.test(trimmed)) {
      continue;
    }
    if (seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    out.push(Number(trimmed));
  }
  out.sort((a, b) => a - b);
  return out.map((n) => String(n));
}

/** Inverse of `parseMerkmalCsvIds` - string[] of ids → canonical CSV string. */
export function joinMerkmalCsvIds(ids: ReadonlyArray<string | number>): string {
  return parseMerkmalCsvIds(ids.join(',')).join(',');
}

/**
 * Current saved Merkmal selection for a field. Prefers the canonical
 * backend-shipped `draftValue` (already sorted ASC, deduped), then falls back
 * to `kMerkmalWerte`. Returns the empty array when nothing is assigned -
 * which serialises back to the "clear all" sentinel.
 */
export function getMerkmalCurrentIds(
  f: ContentMasterWawiExtendedFieldView
): string[] {
  if (typeof f.draftValue === 'string') {
    return parseMerkmalCsvIds(f.draftValue);
  }
  if (Array.isArray(f.kMerkmalWerte)) {
    return parseMerkmalCsvIds(f.kMerkmalWerte.join(','));
  }
  return [];
}

/**
 * Map a list of `kMerkmalWert` ids (any order) into their localised labels,
 * joined with ", " for display. Order matches the order of `ids`; ids not
 * present in `merkmalWertOptions` keep their bare id form so the user is not
 * confused by an empty cell when picklist data is stale.
 */
export function formatMerkmalLabels(
  f: ContentMasterWawiExtendedFieldView,
  ids: ReadonlyArray<string>
): string {
  if (ids.length === 0) {
    return '';
  }
  const byId = new Map<string, string>();
  for (const opt of f.merkmalWertOptions ?? []) {
    if (opt.kMerkmalWert == null) {
      continue;
    }
    const idStr = String(opt.kMerkmalWert);
    const label =
      typeof opt.label === 'string' && opt.label.length > 0 ? opt.label : idStr;
    byId.set(idStr, label);
  }
  return ids.map((idStr) => byId.get(idStr) ?? idStr).join(', ');
}

